import React, { useEffect, useState } from 'react';

export interface LandingFeatureSlide {
  id: string;
  url: string;
}

interface LandingFeatureMediaProps {
  slides: LandingFeatureSlide[];
  alt: string;
}

const CAROUSEL_INTERVAL_MS = 4500;

const LandingFeatureMedia: React.FC<LandingFeatureMediaProps> = ({ slides, alt }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slideKey = slides.map((s) => s.id).join('|');

  useEffect(() => {
    setIndex(0);
  }, [slideKey]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, slides.length, slideKey]);

  if (slides.length === 0) {
    return (
      <div className="landing-feature-image-placeholder">
        <span className="landing-feature-image-placeholder-text">이미지 준비중</span>
      </div>
    );
  }

  if (slides.length === 1) {
    return (
      <div className="landing-feature-image-wrap">
        <img
          src={slides[0].url}
          alt={alt}
          loading="lazy"
          className="landing-feature-image"
        />
      </div>
    );
  }

  return (
    <div
      className="landing-feature-media"
      role="region"
      aria-roledescription="carousel"
      aria-label={alt}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div
        className="landing-feature-carousel"
        onClick={() => setIndex((prev) => (prev + 1) % slides.length)}
        role="button"
        tabIndex={0}
        aria-label="다음 이미지"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIndex((prev) => (prev + 1) % slides.length);
          }
        }}
      >
        <div className="landing-feature-carousel-track">
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              className={`landing-feature-carousel-slide${i === index ? ' is-active' : ''}`}
              aria-hidden={i !== index}
            >
              <img
                src={slide.url}
                alt={`${alt} ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="landing-feature-image"
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="landing-feature-carousel-dots" role="tablist" aria-label="이미지 선택">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`${i + 1}번째 이미지`}
            className={`landing-feature-carousel-dot${i === index ? ' is-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setIndex(i);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LandingFeatureMedia;
